import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

const THEME = { primary: '#1F8A8C', accent: '#F5941F', pageBg: 'radial-gradient(circle at 20% 15%, #2fa3a1 0%, #1F8A8C 45%, #d97b1f 100%)' };

export default function EventRSVP() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [status, setStatus] = useState(null);
  // The DB default for "never answered" is also 'unknown', so an explicit
  // "still don't know" click needs its own flag to show a confirmation
  // instead of silently looking identical to not having answered at all.
  const [answeredUnknown, setAnsweredUnknown] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/portal/event-public/${token}`).then(r => {
      setData(r.data);
      setStatus(r.data.status);
      setAnsweredUnknown(r.data.status === 'unknown' && !!r.data.responded_at);
    }).catch(() => setErr(true));
  }, [token]);

  async function respond(newStatus) {
    setSaving(true);
    try {
      await api.post(`/portal/event-public/${token}`, { status: newStatus });
      setStatus(newStatus);
      setAnsweredUnknown(newStatus === 'unknown');
    } catch {}
    setSaving(false);
  }

  const wrap = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Heebo, Arial, sans-serif', direction: 'rtl', background: THEME.pageBg };

  if (err) return (
    <div style={wrap}>
      <div style={{ background: 'white', borderRadius: 20, padding: 32, color: '#6b7280', textAlign: 'center' }}>הקישור לא תקין או שפג תוקפו</div>
    </div>
  );
  if (!data) return <div style={wrap} />;

  // event_date is a naive "YYYY-MM-DD HH:mm:ss" wall-clock string (no
  // timezone) — formatting it via `new Date(...)` + a timeZone option
  // re-interprets it as a UTC instant and shifts it, so build the display
  // string directly from its components instead.
  const dateMatch = String(data.event_date || '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  const dateStr = dateMatch
    ? `${new Date(+dateMatch[1], +dateMatch[2] - 1, +dateMatch[3]).toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} בשעה ${dateMatch[4]}:${dateMatch[5]}`
    : null;

  return (
    <div style={wrap}>
      <div style={{ width: '100%', maxWidth: 420, borderRadius: 24, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', background: 'white' }}>
        <div style={{ background: `linear-gradient(225deg,${THEME.primary},${THEME.accent})`, padding: '28px 24px', textAlign: 'center', color: 'white' }}>
          <img src="/logos/community_white.png" alt="Community Group" style={{ height: 64, maxWidth: '80%', objectFit: 'contain', marginBottom: 10 }} />
          <div style={{ fontWeight: 700, fontSize: '1.25rem' }}>{data.title}</div>
        </div>
        <div style={{ padding: '28px 24px', textAlign: 'center' }}>
          {dateStr && <div style={{ color: '#374151', fontWeight: 600, marginBottom: 10 }}>{dateStr}</div>}
          {data.description && <div style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: 20 }}>{data.description}</div>}
          {data.pdf_url && (
            <a href={data.pdf_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginBottom: 20, color: THEME.primary, fontWeight: 700, textDecoration: 'underline' }}>
              📄 צפה במודעת הכנס
            </a>
          )}

          {status && status !== 'unknown' ? (
            <div style={{
              borderRadius: 14, padding: '16px', fontWeight: 700, fontSize: '1.05rem',
              background: status === 'attending' ? '#dcfce7' : '#fee2e2',
              color: status === 'attending' ? '#15803d' : '#b91c1c',
            }}>
              {status === 'attending' ? '✅ אישרת הגעה' : '❌ ציינת שלא תגיע'}
            </div>
          ) : answeredUnknown ? (
            <div style={{ borderRadius: 14, padding: '16px', fontWeight: 700, fontSize: '1.05rem', background: '#fef9c3', color: '#a16207' }}>
              🤷 ציינת שעדיין לא ידוע
            </div>
          ) : (
            <>
              <div style={{ color: '#1f2937', fontWeight: 600, marginBottom: 14 }}>האם תגיע/י?</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button disabled={saving} onClick={() => respond('attending')} style={{ flex: 1, background: '#15803d', color: 'white', border: 'none', padding: '14px 0', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>
                  ✅ מגיע/ה
                </button>
                <button disabled={saving} onClick={() => respond('not_attending')} style={{ flex: 1, background: '#dc2626', color: 'white', border: 'none', padding: '14px 0', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>
                  ❌ לא מגיע/ה
                </button>
              </div>
              <button disabled={saving} onClick={() => respond('unknown')} style={{ width: '100%', marginTop: 10, background: '#fef9c3', color: '#a16207', border: 'none', padding: '12px 0', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem' }}>
                🤷 עדיין לא יודע/ת
              </button>
            </>
          )}

          {(status && status !== 'unknown' || answeredUnknown) && (
            <button onClick={() => { setStatus('unknown'); setAnsweredUnknown(false); }} style={{ marginTop: 14, background: 'none', border: 'none', color: '#9ca3af', fontSize: '0.82rem', textDecoration: 'underline', cursor: 'pointer' }}>
              לשנות תשובה
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
